import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import _ from "lodash";
import moment from "moment-timezone";
import ClipLoader from "react-spinners/ClipLoader";
import { css } from "@emotion/core";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import smartParkService from "../../axios";
import { login_ } from "../../reducers/login";
import { checkPermissions } from "../../utils/checkPermissions.js";
import styles from "../../components/components.js";

import AttributeDetailsCard from "./Components/AttributeDetailsCard.jsx";
import AttributeInfoCard from "./Components/AttributeInfoCard.jsx";

import "../../assets/scss/App.scss";

const override = css`
  display: block;
  margin: 250px auto;
  border-color: #0076bb;
`;

const useStyles = makeStyles(styles);

const AttributeDetails = () => {
  const classes = useStyles();

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const sensor = params.get("sensorId");
  const attribute = params.get("attributeId");

  const loginDetails = useSelector(login_);
  const userid = _.get(loginDetails, ["UserID"], "");

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isFirstLoad = useRef(true);
  const initialLoadCompleted = useRef(false);

  const [loading, setLoading] = useState(true);

  const [sensorData, setSensorData] = useState({});
  const [chartData, setChartData] = useState({});

  const [optionsData, setOptionsData] = useState({
    start: null,
    end: null,

    aggregationMethod: "raw",

    aggregationOptions: {
      Raw: "raw",
    },

    samplingPeriod: null,
    samplingOptions: {},

    page: 0,
    limit: 10,
    totalPages: null,
  });

  const buildQueryParams = (includeDates = true) => {
    let query =
      `limit=${optionsData.limit}` +
      `&page=${optionsData.page + 1}` +
      `&method=${optionsData.aggregationMethod}` +
      `&timezone=${userTimezone}`;

    if (
      includeDates &&
      optionsData.start !== null &&
      optionsData.end !== null
    ) {
      query += `&start=${optionsData.start}` + `&end=${optionsData.end}`;
    }

    return query;
  };

  const fetchData = async (includeDates = true) => {
    const url =
      `/api/sensors/dot-sensor/viewreading/details/${sensor}/${attribute}/?` +
      buildQueryParams(includeDates);

    const response = await smartParkService.get(url, {
      headers: {
        userid,
      },
    });

    if (response.data.data) {
      const { report } = response.data.data.details;

      const { metadata } = response.data.data;

      setSensorData(response.data.data);

      setOptionsData((prev) => {
        const updated = {
          ...prev,
          totalPages: metadata.total,
        };

        // Only initialize dates once from API
        if (isFirstLoad.current) {
          updated.start = report?.start ? moment(report.start).valueOf() : null;

          updated.end = report?.end ? moment(report.end).valueOf() : null;
        }

        return updated;
      });
    }
  };

  const fetchGraphData = async (includeDates = true) => {
    const url =
      `/api/sensors/dot-sensor/viewreading/graph/${sensor}/${attribute}/?` +
      buildQueryParams(includeDates);

    const response = await smartParkService.get(url, {
      headers: {
        userid,
      },
    });

    if (response.data.data) {
      setChartData(response.data.data);
    }
  };

  const loadData = async (includeDates = true) => {
    try {
      setLoading(true);

      await Promise.all([
        fetchData(includeDates),
        fetchGraphData(includeDates),
      ]);
    } catch (error) {
      console.error("Failed to fetch attribute details", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - NO dates
  useEffect(() => {
    if (!sensor || !attribute || !userid) return;

    if (initialLoadCompleted.current) return;

    initialLoadCompleted.current = true;

    loadData(false).finally(() => {
      isFirstLoad.current = false;
    });
  }, [sensor, attribute, userid]);

  // User changes only
  useEffect(() => {
    if (isFirstLoad.current) return;

    loadData(true);
  }, [
    optionsData.page,
    optionsData.limit,
    optionsData.aggregationMethod,
    optionsData.start,
    optionsData.end,
  ]);

  const changeHandler = (e) => {
    if (e.type === "pageChange") {
      setOptionsData((prev) => ({
        ...prev,
        page: e.params.newpage,
      }));
    }

    if (e.type === "refresh") {
      loadData(true);
    }

    if (e.type === "limitChange") {
      setOptionsData((prev) => ({
        ...prev,
        page: 0,
        limit: e.params.newLimit,
      }));
    }

    if (e.type === "dateChange") {
      setOptionsData((prev) => ({
        ...prev,
        page: 0,
        start: e.params.start.valueOf(),
        end: e.params.end.valueOf(),
      }));
    }
  };

  const hasPermission = checkPermissions({
    permission: "Configuration",
    permissionType: "read",
  });

  if (!hasPermission) {
    return (
      <div
        style={{
          color: "red",
          position: "absolute",
          top: "50%",
          left: "40%",
          fontSize: "20px",
          transform: "translate(-50%, -50%)",
        }}
      >
        You do not have read permissions. Please contact Admin
      </div>
    );
  }

  if (loading) {
    return (
      <ClipLoader color="#0076bb" loading={loading} css={override} size={60} />
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.contentPage}>
        <section className="content-header">
          <h3 className="float-left">Attribute Information</h3>

          <div className="float-right">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/overview">Home</a>
              </li>

              <li className="breadcrumb-item">
                <a className="active" href="#">
                  Attribute Details
                </a>
              </li>
            </ol>
          </div>
        </section>

        <section className="content">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <AttributeDetailsCard data={sensorData} />
                </Grid>

                <Grid item xs={9}>
                  <AttributeInfoCard
                    sensors={sensorData.readings}
                    chartData={chartData.readings}
                    optionsData={optionsData}
                    changeHandler={changeHandler}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </section>
      </div>
    </div>
  );
};

export default AttributeDetails;
